import { useLocation } from "react-router"
import { type ScriptHTMLAttributes, useEffect } from "react"

export type Umami = {
	track: (
		event?: string | object | ((props: object) => object),
		eventData?: object,
	) => void
	identify: (data: string | object, properties?: object) => void
}

export type UmamiQueue = Array<(umami: Umami) => void>

declare global {
	interface Window {
		umami: Umami | undefined
		umamiQueue: UmamiQueue | undefined
	}
}

export function useUmami() {
	const umami = () => {
		if (window.umami && (window.umamiQueue || []).length === 0) {
			return window.umami
		}

		return new Proxy({} as Umami, {
			get(target, prop) {
				return (...args: any[]) => {
					window.umamiQueue = [
						...(window.umamiQueue || []),
						umami => {
							const property = Reflect.get(umami, prop)
							if (typeof property === "function") {
								property.bind(umami)(...args)
							}
						},
					]
				}
			},
		})
	}

	return { umami }
}

export function useTrackPageview() {
	const { pathname } = useLocation()
	const { umami } = useUmami()

	useEffect(() => {
		umami().track()
	}, [pathname])
}

export function UmamiScript(
	props: {
		"data-website-id": string
	} & ScriptHTMLAttributes<HTMLScriptElement>,
) {
	useEffect(() => {
		const loadAnalytics = () => {
			const script = document.createElement("script")
			script.src = "https://analytics.konam.dev/script.js"
			script.async = true
			script.setAttribute("data-website-id", props["data-website-id"])
			script.setAttribute("data-auto-track", "false")
			if (props.nonce) {
				script.nonce = props.nonce as string
			}

			script.addEventListener("load", () => {
				if (window.umamiQueue) {
					while (window.umamiQueue.length > 0) {
						const fn = window.umamiQueue.shift()
						if (fn && window.umami) {
							fn(window.umami)
						}
					}
				}
			})

			document.head.appendChild(script)
		}

		if ("requestIdleCallback" in window) {
			requestIdleCallback(loadAnalytics, { timeout: 3000 })
		} else {
			setTimeout(loadAnalytics, 3000)
		}
	}, [])

	return null
}
